import React, { useEffect, useRef, useState } from 'react';

// ─── Constants ────────────────────────────────────────────────────────────────

const RED      = '#bf1e2e';
const NAVY     = '#014c8d';
const BG       = '#080d16';
const SURFACE  = '#0d1525';
const BORDER   = 'rgba(255,255,255,0.08)';
const DIM      = 'rgba(255,255,255,0.50)';
const DESIGN_W = 960;
const DESIGN_H = DESIGN_W * 9 / 16; // 540

const PASSWORD = 'raptors905';

const RAPTORS_LOGO = 'https://upload.wikimedia.org/wikipedia/en/4/4b/Raptors_905_logo.svg';

type SlideId = 'cover' | 'reality' | 'program' | 'howitworks' | 'partnership' | 'nextsteps';

interface Slide { id: SlideId; label: string; }

const SLIDES: Slide[] = [
  { id: 'cover',       label: '01  Cover' },
  { id: 'reality',     label: '02  The Reality' },
  { id: 'program',     label: '03  What We Build' },
  { id: 'howitworks',  label: '04  How It Works' },
  { id: 'partnership', label: '05  Partnership' },
  { id: 'nextsteps',   label: '06  Next Steps' },
];

// ─── Scale wrapper ────────────────────────────────────────────────────────────

function ScaledSlide({ children }: { children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setScale(el.offsetWidth / DESIGN_W);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={ref} style={{ width: '100%', height: DESIGN_H * scale, position: 'relative' }}>
      <div style={{
        width: DESIGN_W, height: DESIGN_H,
        transform: `scale(${scale})`, transformOrigin: 'top left',
        position: 'absolute', top: 0, left: 0,
      }}>
        {children}
      </div>
    </div>
  );
}

// ─── Shared slide shell ───────────────────────────────────────────────────────

function SlideShell({
  tag, children, center = false,
}: {
  tag?: string;
  children: React.ReactNode;
  center?: boolean;
}) {
  return (
    <div style={{
      width: '100%', height: '100%', background: BG,
      display: 'flex', flexDirection: 'column',
      justifyContent: center ? 'center' : 'flex-start',
      alignItems: center ? 'center' : 'stretch',
      padding: '48px 64px', position: 'relative',
      overflow: 'hidden', boxSizing: 'border-box',
    }}>
      {/* Grid texture */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: `
          linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)
        `,
        backgroundSize: '60px 60px',
      }} />
      {/* Navy top bar with red accent */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 4, background: NAVY }} />
      <div style={{ position: 'absolute', top: 0, left: 0, width: 80, height: 4, background: RED }} />
      {/* Slide tag */}
      {tag && (
        <div style={{
          position: 'absolute', top: 18, left: 64,
          fontSize: 9, fontWeight: 800, letterSpacing: 3.5,
          color: NAVY, textTransform: 'uppercase',
          fontFamily: "'Barlow Condensed', sans-serif",
        }}>{tag}</div>
      )}
      {/* Footer */}
      <div style={{
        position: 'absolute', bottom: 10, left: 64, right: 48,
        display: 'flex', alignItems: 'center',
      }}>
        <div style={{ opacity: 0.70 }}><BthdMark markHeight={14} /></div>
        <div style={{ width: 1, height: 13, background: 'rgba(255,255,255,0.18)', margin: '0 8px' }} />
        <span style={{
          fontSize: 9, fontWeight: 600, color: 'rgba(255,255,255,0.65)',
          letterSpacing: 0.4,
        }}>Bigger Than Hoop Dreams Inc.</span>
      </div>
      {children}
    </div>
  );
}

function BthdMark({ markHeight }: { markHeight: number }) {
  const canvasH  = markHeight / 0.32;
  const markW    = canvasH * 0.82;
  const clipTop  = canvasH * 0.34;
  const clipLeft = canvasH * 0.09;
  return (
    <div style={{ height: markHeight, width: markW, overflow: 'hidden', flexShrink: 0, position: 'relative' }}>
      <img
        src="/bthd-icon.png"
        alt="BTHD"
        style={{ height: canvasH, width: 'auto', position: 'absolute', top: -clipTop, left: -clipLeft, display: 'block' }}
      />
    </div>
  );
}

function SlideHeading({ children, size = 46 }: { children: React.ReactNode; size?: number }) {
  return (
    <h2 style={{
      margin: '0 0 8px', fontSize: size, fontWeight: 900,
      lineHeight: 1.05, color: '#fff', textTransform: 'uppercase',
      letterSpacing: -0.5, fontFamily: "'Barlow Condensed', sans-serif",
    }}>{children}</h2>
  );
}

function RedAccent({ children }: { children: React.ReactNode }) {
  return <span style={{ color: RED }}>{children}</span>;
}

function BlueAccent({ children }: { children: React.ReactNode }) {
  return <span style={{ color: '#4a9eff' }}>{children}</span>;
}

function Tag({ children, navy = false }: { children: React.ReactNode; navy?: boolean }) {
  const c = navy ? NAVY : RED;
  return (
    <div style={{
      display: 'inline-flex', alignItems: 'center',
      background: `${c}22`, border: `1px solid ${c}55`,
      borderRadius: 4, padding: '3px 10px',
      fontSize: 9, fontWeight: 800, letterSpacing: 2,
      color: c, textTransform: 'uppercase', marginBottom: 14,
      fontFamily: "'Barlow Condensed', sans-serif",
    }}>{children}</div>
  );
}

// ─── Slides ───────────────────────────────────────────────────────────────────

function CoverSlide() {
  return (
    <SlideShell center>
      <div style={{
        position: 'absolute', top: '35%', left: '50%', transform: 'translate(-50%,-50%)',
        width: 700, height: 320,
        background: `radial-gradient(ellipse, ${NAVY}33 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        {/* Dual logo lockup */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 32, marginBottom: 22,
        }}>
          <BthdMark markHeight={44} />
          <div style={{ width: 1, height: 44, background: 'rgba(255,255,255,0.2)' }} />
          <img
            src={RAPTORS_LOGO}
            alt="Raptors 905"
            style={{ height: 56, width: 'auto' }}
          />
        </div>

        <div style={{
          fontSize: 10, fontWeight: 800, letterSpacing: 4,
          color: RED, textTransform: 'uppercase', marginBottom: 16,
          fontFamily: "'Barlow Condensed', sans-serif",
        }}>Partnership Proposal</div>

        <h1 style={{
          fontSize: 60, fontWeight: 900, lineHeight: 1.0,
          color: '#fff', textTransform: 'uppercase',
          letterSpacing: -1, margin: '0 0 10px',
          fontFamily: "'Barlow Condensed', sans-serif",
        }}>
          BTHD <span style={{ color: 'rgba(255,255,255,0.25)' }}>×</span><br />
          <span style={{ color: '#4a9eff' }}>Raptors 905</span>
        </h1>

        <div style={{
          fontSize: 18, fontWeight: 700, color: 'rgba(255,255,255,0.60)',
          letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 28,
          fontFamily: "'Barlow Condensed', sans-serif",
        }}>Developing the Whole Athlete</div>

        <div style={{
          display: 'inline-block', padding: '1px 0',
          borderTop: `1px solid rgba(255,255,255,0.15)`,
          borderBottom: `1px solid rgba(255,255,255,0.15)`,
        }}>
          <p style={{
            fontSize: 15, color: DIM, margin: '10px 0',
            fontStyle: 'italic', letterSpacing: 0.5,
          }}>
            "The game ends. The journey doesn't."
          </p>
        </div>
      </div>
    </SlideShell>
  );
}

function RealitySlide() {
  const points = [
    {
      stat: '4.8',
      unit: 'years',
      label: 'Average professional basketball career. Most G League players will never play an NBA game — and few have a plan for what comes next.',
    },
    {
      stat: null,
      label: 'Identity, purpose, and career capital are built during the career — not after it ends. Waiting until retirement is waiting too long.',
    },
    {
      stat: null,
      label: 'The NBA and NBPA have jointly funded mental wellness infrastructure. The league itself acknowledges this is a systemic challenge.',
    },
  ];

  return (
    <SlideShell tag="The Reality">
      <div style={{ marginTop: 28 }}>
        <Tag>G League Context</Tag>
        <SlideHeading size={42}>Most Players Leave<br /><RedAccent>Without a Plan.</RedAccent></SlideHeading>
        <p style={{ color: DIM, fontSize: 13.5, marginBottom: 16, maxWidth: 640, lineHeight: 1.65 }}>
          Professional basketball is a career. Like every career, it ends.
          What athletes do with the years inside it determines everything that follows.
        </p>
        <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
          {points.map(({ stat, unit, label }, i) => (
            <div key={i} style={{
              flex: 1, background: SURFACE,
              border: `1px solid ${BORDER}`, borderTop: `2px solid ${i === 0 ? RED : NAVY}`,
              borderRadius: 10, padding: '20px 20px 18px',
            }}>
              {stat && (
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 10 }}>
                  <div style={{
                    fontSize: 52, fontWeight: 900, color: RED, lineHeight: 1,
                    fontFamily: "'Barlow Condensed', sans-serif",
                  }}>{stat}</div>
                  <div style={{
                    fontSize: 16, fontWeight: 700, color: 'rgba(255,255,255,0.45)',
                    textTransform: 'uppercase', letterSpacing: 1,
                    fontFamily: "'Barlow Condensed', sans-serif",
                  }}>{unit}</div>
                </div>
              )}
              {!stat && (
                <div style={{
                  width: 28, height: 3, background: `${NAVY}88`,
                  borderRadius: 2, marginBottom: 12,
                }} />
              )}
              <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.78)', lineHeight: 1.65 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

function ProgramSlide() {
  const stages = [
    { num: '01', title: 'Who You Are', body: 'Identity assessment. Behavioural strengths, values, and athletic self-concept.', color: RED },
    { num: '02', title: 'Where You\'re Going', body: 'Career capital mapping. Transferable skills and transition pathways.', color: NAVY },
    { num: '03', title: 'How You Lead', body: 'Communication, team dynamics, and personal brand building.', color: '#1a6b3c' },
    { num: '04', title: 'What You Leave Behind', body: 'Legacy, community, and life architecture beyond the game.', color: '#7a3c8a' },
  ];

  return (
    <SlideShell tag="What We Build Together">
      <div style={{ marginTop: 24 }}>
        <Tag>Structured 15-Week Program</Tag>
        <SlideHeading size={40}>A Development Program<br /><BlueAccent>Built for Competing Athletes.</BlueAccent></SlideHeading>
        <p style={{ color: DIM, fontSize: 13, marginBottom: 18, maxWidth: 700, lineHeight: 1.65 }}>
          BTHD is not a retirement program. It is a development program — designed for athletes
          still competing, using spaced learning and scenario-based reflection that fits around the
          demands of a professional season.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12 }}>
          {stages.map(({ num, title, body, color }) => (
            <div key={num} style={{
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderTop: `3px solid ${color}`, borderRadius: 10,
              padding: '16px 16px 14px',
            }}>
              <div style={{
                fontSize: 28, fontWeight: 900, color, lineHeight: 1, marginBottom: 8,
                fontFamily: "'Barlow Condensed', sans-serif",
              }}>{num}</div>
              <div style={{
                fontSize: 13, fontWeight: 800, color: '#fff', textTransform: 'uppercase',
                letterSpacing: 0.3, marginBottom: 6, lineHeight: 1.25,
                fontFamily: "'Barlow Condensed', sans-serif",
              }}>{title}</div>
              <div style={{ fontSize: 11.5, color: DIM, lineHeight: 1.55 }}>{body}</div>
            </div>
          ))}
        </div>
        <div style={{
          marginTop: 14, padding: '12px 18px',
          background: `${NAVY}18`, border: `1px solid ${NAVY}33`,
          borderRadius: 8,
          fontSize: 12.5, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6,
        }}>
          <strong style={{ color: '#fff' }}>15 weeks.</strong>{' '}
          Spaced learning, scenario-based reflection, identity development.
          Athletes complete work on their own schedule — between practices, flights, and games.
        </div>
      </div>
    </SlideShell>
  );
}

function HowItWorksSlide() {
  const CalendarIcon = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#4a9eff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
      <line x1="16" y1="2" x2="16" y2="6"/>
      <line x1="8" y1="2" x2="8" y2="6"/>
      <line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );
  const ChatIcon = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#4a9eff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    </svg>
  );
  const TargetIcon = () => (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#4a9eff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10"/>
      <circle cx="12" cy="12" r="6"/>
      <circle cx="12" cy="12" r="2"/>
    </svg>
  );
  const pillars: { icon: React.ReactNode; title: string; body: string }[] = [
    {
      icon: <CalendarIcon />,
      title: 'Fits the Season Schedule',
      body: 'Spaced learning is designed around the demands of a professional season. No mandatory group sessions, no conflicts with practice or travel.',
    },
    {
      icon: <ChatIcon />,
      title: 'Weekly Touchpoints',
      body: 'Reflection and scenario work athletes complete on their own time. Short, intentional, and cumulative — building over 15 weeks.',
    },
    {
      icon: <TargetIcon />,
      title: '1:1 Consultative Sessions',
      body: 'Personalized, confidential, and athlete-led. Athletes direct the conversation. Depth happens here.',
    },
  ];

  return (
    <SlideShell tag="How It Works With 905">
      <div style={{ marginTop: 30, display: 'flex', gap: 48, alignItems: 'flex-start' }}>
        <div style={{ flex: '0 0 300px' }}>
          <Tag navy>Program Delivery</Tag>
          <SlideHeading size={40}>Built Around<br /><BlueAccent>The G League Life.</BlueAccent></SlideHeading>
          <p style={{ color: DIM, fontSize: 13.5, lineHeight: 1.7, marginTop: 12 }}>
            The program works with the reality of a professional athlete's schedule —
            not against it. Three delivery mechanisms, all athlete-controlled.
          </p>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14, justifyContent: 'center' }}>
          {pillars.map(({ icon, title, body }) => (
            <div key={title} style={{
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderLeft: `3px solid ${NAVY}`,
              borderRadius: 10, padding: '18px 20px',
              display: 'flex', gap: 16, alignItems: 'flex-start',
            }}>
              <div style={{ fontSize: 26, flexShrink: 0, lineHeight: 1 }}>{icon}</div>
              <div>
                <div style={{
                  fontFamily: "'Barlow Condensed', sans-serif",
                  fontSize: 15, fontWeight: 900, color: '#fff',
                  textTransform: 'uppercase', letterSpacing: 0.3, marginBottom: 6,
                }}>{title}</div>
                <div style={{ fontSize: 12.5, color: DIM, lineHeight: 1.65 }}>{body}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

function PartnershipSlide() {
  return (
    <SlideShell tag="The Partnership">
      <div style={{ marginTop: 30, display: 'flex', gap: 20, alignItems: 'stretch', height: 'calc(100% - 80px)' }}>

        {/* BTHD side */}
        <div style={{
          flex: 1, background: `${RED}0e`, border: `1px solid ${RED}2a`,
          borderTop: `3px solid ${RED}`, borderRadius: 12, padding: '28px 28px 24px',
          display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ marginBottom: 8 }}><BthdMark markHeight={26} /></div>
          <div style={{
            fontSize: 10, fontWeight: 800, letterSpacing: 2.5, color: RED,
            textTransform: 'uppercase', marginBottom: 12,
            fontFamily: "'Barlow Condensed', sans-serif",
          }}>BTHD Provides</div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              'The platform — mobile app and web portal, built for athletes',
              'The program — 15-week curriculum, scenario-based and evidence-informed',
              'The AI — personalized coaching, transition analysis, identity development',
              'The infrastructure — assessments, progress tracking, reporting',
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: `${RED}22`, border: `1px solid ${RED}55`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1,
                }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: RED }} />
                </div>
                <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.80)', lineHeight: 1.55 }}>{item}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <div style={{
            fontFamily: "'Barlow Condensed', sans-serif",
            fontSize: 28, fontWeight: 900, color: 'rgba(255,255,255,0.15)',
          }}>+</div>
        </div>

        {/* 905 side */}
        <div style={{
          flex: 1, background: `${NAVY}18`, border: `1px solid ${NAVY}44`,
          borderTop: `3px solid ${NAVY}`, borderRadius: 12, padding: '28px 28px 24px',
          display: 'flex', flexDirection: 'column',
        }}>
          <img src={RAPTORS_LOGO} alt="Raptors 905" style={{ height: 36, width: 'auto', marginBottom: 8 }} />
          <div style={{
            fontSize: 10, fontWeight: 800, letterSpacing: 2.5, color: '#4a9eff',
            textTransform: 'uppercase', marginBottom: 12,
            fontFamily: "'Barlow Condensed', sans-serif",
          }}>Raptors 905 Provides</div>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              'The athletes — a roster of professional players in a critical development window',
              'The mandate — organizational commitment to whole-athlete development',
              'The relationship — trust that unlocks athlete buy-in and participation',
              'The context — a professional environment that makes the work credible',
            ].map((item, i) => (
              <div key={i} style={{
                display: 'flex', gap: 10, alignItems: 'flex-start',
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%',
                  background: `${NAVY}33`, border: `1px solid ${NAVY}88`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1,
                }}>
                  <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#4a9eff' }} />
                </div>
                <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.80)', lineHeight: 1.55 }}>{item}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Together bar */}
      <div style={{
        position: 'absolute', bottom: 28, left: 64, right: 64,
        background: 'rgba(255,255,255,0.03)', border: `1px solid ${BORDER}`,
        borderRadius: 8, padding: '12px 24px', textAlign: 'center',
      }}>
        <span style={{
          fontFamily: "'Barlow Condensed', sans-serif",
          fontSize: 15, fontWeight: 900, color: '#fff',
          letterSpacing: 0.5, textTransform: 'uppercase',
        }}>
          Together —{' '}
          <span style={{ color: '#4a9eff' }}>a complete athlete development infrastructure.</span>
        </span>
      </div>
    </SlideShell>
  );
}

function NextStepsSlide() {
  return (
    <SlideShell center>
      <div style={{
        position: 'absolute', top: '35%', left: '50%', transform: 'translate(-50%,-50%)',
        width: 600, height: 300,
        background: `radial-gradient(ellipse, ${NAVY}28 0%, transparent 70%)`,
        pointerEvents: 'none',
      }} />
      <div style={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
        {/* Dual logo */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 28, marginBottom: 22 }}>
          <BthdMark markHeight={36} />
          <div style={{ width: 1, height: 36, background: 'rgba(255,255,255,0.2)' }} />
          <img src={RAPTORS_LOGO} alt="Raptors 905" style={{ height: 46, width: 'auto' }} />
        </div>

        <div style={{
          fontSize: 10, fontWeight: 800, letterSpacing: 4, color: RED,
          textTransform: 'uppercase', marginBottom: 14,
          fontFamily: "'Barlow Condensed', sans-serif",
        }}>Next Steps</div>

        <h2 style={{
          fontSize: 54, fontWeight: 900, color: '#fff',
          textTransform: 'uppercase', letterSpacing: -1,
          lineHeight: 1.0, margin: '0 0 30px',
          fontFamily: "'Barlow Condensed', sans-serif",
        }}>
          Let's Build Something<br />
          <span style={{ color: '#4a9eff' }}>Together.</span>
        </h2>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 500, margin: '0 auto 32px' }}>
          {[
            'Introductory conversation — align on scope, timing, and roster size',
            'Starter cohort — one group of athletes through the full 15-week program',
            'Evaluate outcomes — together, and decide on long-term structure',
          ].map((s, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              background: SURFACE, border: `1px solid ${BORDER}`,
              borderRadius: 8, padding: '10px 16px', textAlign: 'left',
            }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: `${NAVY}44`, border: `1px solid ${NAVY}88`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <span style={{ fontSize: 10, fontWeight: 900, color: '#4a9eff' }}>{i + 1}</span>
              </div>
              <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.82)', lineHeight: 1.4 }}>{s}</span>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 14, flexWrap: 'wrap' }}>
          <a href="mailto:harry@bthd.app" style={{
            display: 'inline-block', padding: '10px 24px',
            background: NAVY, border: `1px solid ${NAVY}`,
            borderRadius: 6, fontSize: 13, fontWeight: 700,
            color: '#fff', textDecoration: 'none', letterSpacing: 0.5,
          }}>harry@bthd.app</a>
          <a href="https://bthd.app" style={{
            display: 'inline-block', padding: '10px 24px',
            background: 'transparent', border: `1px solid rgba(255,255,255,0.15)`,
            borderRadius: 6, fontSize: 13, fontWeight: 700,
            color: 'rgba(255,255,255,0.70)', textDecoration: 'none', letterSpacing: 0.5,
          }}>bthd.app</a>
        </div>
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', letterSpacing: 0.5 }}>
          416-722-3660 &nbsp;·&nbsp; Bigger Than Hoop Dreams Inc. &nbsp;·&nbsp; Kitchener, Ontario, Canada
        </div>
      </div>
    </SlideShell>
  );
}

// ─── Slide renderer ───────────────────────────────────────────────────────────

function renderSlide(id: SlideId) {
  switch (id) {
    case 'cover':       return <CoverSlide />;
    case 'reality':     return <RealitySlide />;
    case 'program':     return <ProgramSlide />;
    case 'howitworks':  return <HowItWorksSlide />;
    case 'partnership': return <PartnershipSlide />;
    case 'nextsteps':   return <NextStepsSlide />;
  }
}

// ─── Password gate ────────────────────────────────────────────────────────────

function PasswordGate({ onUnlock }: { onUnlock: () => void }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(false);

  const attempt = () => {
    if (value.trim() === PASSWORD) {
      onUnlock();
    } else {
      setError(true);
      setValue('');
      setTimeout(() => setError(false), 1400);
    }
  };

  return (
    <div style={{
      minHeight: '100vh', background: BG,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: "'Barlow Condensed', 'Barlow', sans-serif",
    }}>
      <div style={{
        width: 360, textAlign: 'center',
        background: SURFACE, border: `1px solid ${BORDER}`,
        borderTop: `3px solid ${NAVY}`, borderRadius: 12,
        padding: '40px 36px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 20, marginBottom: 10 }}>
          <BthdMark markHeight={32} />
          <div style={{ width: 1, height: 32, background: 'rgba(255,255,255,0.2)' }} />
          <img src={RAPTORS_LOGO} alt="Raptors 905" style={{ height: 40, width: 'auto' }} />
        </div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.50)', letterSpacing: 0.5, marginBottom: 22 }}>
          Bigger Than Hoop Dreams Inc.
        </div>
        <div style={{
          fontSize: 11, fontWeight: 800, letterSpacing: 3, color: RED,
          textTransform: 'uppercase', marginBottom: 6,
        }}>Confidential</div>
        <div style={{ fontSize: 20, fontWeight: 900, color: '#fff', marginBottom: 24, textTransform: 'uppercase', lineHeight: 1.2 }}>
          BTHD × Raptors 905<br />Partnership Deck
        </div>
        <input
          type="password"
          value={value}
          placeholder="Enter access code"
          onChange={e => setValue(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && attempt()}
          style={{
            width: '100%', padding: '11px 14px',
            background: '#080d16', border: `1px solid ${error ? RED : BORDER}`,
            borderRadius: 6, color: '#fff', fontSize: 14,
            outline: 'none', boxSizing: 'border-box',
            transition: 'border-color 0.2s',
            fontFamily: 'inherit',
          }}
        />
        {error && (
          <div style={{ fontSize: 12, color: RED, marginTop: 8 }}>Incorrect access code.</div>
        )}
        <button
          onClick={attempt}
          style={{
            marginTop: 14, width: '100%', padding: '11px',
            background: NAVY, border: 'none', borderRadius: 6,
            color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: 'pointer', letterSpacing: 0.5, fontFamily: 'inherit',
          }}
        >
          Access Deck
        </button>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', marginTop: 20, lineHeight: 1.7 }}>
          Bigger Than Hoop Dreams Inc.<br />
          For review purposes only · Confidential
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function Raptors905DeckPage() {
  const [unlocked, setUnlocked] = useState(false);
  const [current, setCurrent] = useState<SlideId>('cover');

  const idx  = SLIDES.findIndex(s => s.id === current);
  const prev = idx > 0 ? SLIDES[idx - 1].id : null;
  const next = idx < SLIDES.length - 1 ? SLIDES[idx + 1].id : null;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (!unlocked) return;
      if (e.key === 'ArrowRight' && next) setCurrent(next);
      if (e.key === 'ArrowLeft'  && prev) setCurrent(prev);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [unlocked, next, prev]);

  if (!unlocked) return <PasswordGate onUnlock={() => setUnlocked(true)} />;

  return (
    <div style={{
      minHeight: '100vh', background: '#050911',
      fontFamily: "'Barlow Condensed', 'Barlow', sans-serif",
      display: 'flex', flexDirection: 'column',
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 0,
        background: '#0a101e', borderBottom: '1px solid rgba(255,255,255,0.06)',
        padding: '0 24px', height: 48, flexShrink: 0,
        overflowX: 'auto',
      }}>
        <div style={{ marginRight: 8, opacity: 0.90 }}><BthdMark markHeight={18} /></div>
        <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)', margin: '0 8px', flexShrink: 0 }} />
        <img src={RAPTORS_LOGO} alt="Raptors 905"
          style={{ height: 26, width: 'auto', marginRight: 20, opacity: 0.80, flexShrink: 0 }} />
        {SLIDES.map(s => (
          <button
            key={s.id}
            onClick={() => setCurrent(s.id)}
            style={{
              padding: '0 14px', height: '100%', background: 'none',
              border: 'none', borderBottom: `2px solid ${s.id === current ? NAVY : 'transparent'}`,
              color: s.id === current ? '#fff' : 'rgba(255,255,255,0.4)',
              fontSize: 11, fontWeight: s.id === current ? 800 : 600,
              cursor: 'pointer', whiteSpace: 'nowrap', letterSpacing: 0.5,
              fontFamily: "'Barlow Condensed', sans-serif",
              transition: 'color 0.15s',
            }}
          >{s.label}</button>
        ))}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexShrink: 0, paddingLeft: 16 }}>
          <button
            onClick={() => prev && setCurrent(prev)} disabled={!prev}
            style={{
              padding: '4px 12px', background: SURFACE,
              border: `1px solid ${BORDER}`, borderRadius: 4,
              color: prev ? '#fff' : 'rgba(255,255,255,0.2)',
              fontSize: 12, cursor: prev ? 'pointer' : 'default',
              fontFamily: 'inherit',
            }}>←</button>
          <button
            onClick={() => next && setCurrent(next)} disabled={!next}
            style={{
              padding: '4px 12px', background: SURFACE,
              border: `1px solid ${BORDER}`, borderRadius: 4,
              color: next ? '#fff' : 'rgba(255,255,255,0.2)',
              fontSize: 12, cursor: next ? 'pointer' : 'default',
              fontFamily: 'inherit',
            }}>→</button>
        </div>
      </div>

      {/* Slide */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '32px 48px' }}>
        <div style={{ maxWidth: 1100, width: '100%', margin: '0 auto' }}>
          <ScaledSlide>
            {renderSlide(current)}
          </ScaledSlide>
        </div>
        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 11, color: 'rgba(255,255,255,0.2)', letterSpacing: 1 }}>
          {idx + 1} / {SLIDES.length}
        </div>
      </div>
    </div>
  );
}
